import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { Card, Message, Page, Shell } from '../components/PageTheme';
import placePledgeLogoBold from '../assets/place-pledge-logo-bold.svg';

const LoginPage = styled(Page)`
  position: relative;
  display: grid;
  align-content: center;
  min-height: 100dvh;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 640px) {
    min-height: 100svh;
    padding-top: 1rem;
    padding-bottom: calc(7rem + env(safe-area-inset-bottom));
  }
`;

const LoginShell = styled(Shell)`
  width: min(520px, 100%);
  display: grid;
  gap: clamp(1.5rem, 5vh, 3rem);
`;

const LoginCard = styled(Card)`
  text-align: center;
`;

const BrandLogo = styled.img`
  display: block;
  width: 100%;
  max-width: 920px;
  height: auto;
  justify-self: center;

  @media (max-width: 520px) {
    width: min(100%, 94vw);
    max-width: 430px;
  }
`;

const BrandSubheading = styled.p`
  width: min(430px, 100%);
  margin: -1.65rem auto 0;
  color: rgba(226, 232, 240, 0.82);
  font-size: clamp(1rem, 2.4vw, 1.18rem);
  font-weight: 750;
  line-height: 1.45;
  text-align: center;
`;

const GoogleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;
  width: min(340px, 100%);
  min-height: 50px;
  margin-top: 1.15rem;
  padding: 0.8rem 1rem;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #fbbf24, #fb923c);
  color: #111827;
  font-size: 1rem;
  font-weight: 950;
  cursor: pointer;
  box-shadow: 0 18px 45px rgba(251, 146, 60, 0.25);

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  img {
    width: 18px;
    height: 18px;
  }
`;

const DemoButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;
  width: min(340px, 100%);
  min-height: 48px;
  margin-top: 0.75rem;
  padding: 0.75rem 1rem;
  border: 1px solid rgba(226, 232, 240, 0.16);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  color: #f8fafc;
  font-size: 1rem;
  font-weight: 900;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.14);
  }
`;

const FaqLink = styled(Link)`
  display: inline-flex;
  position: absolute;
  top: calc(1.25rem + env(safe-area-inset-top));
  right: 1.25rem;
  z-index: 2;
  min-height: 42px;
  align-items: center;
  padding: 0 1rem;
  border: 1px solid rgba(226, 232, 240, 0.14);
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.44);
  color: rgba(226, 232, 240, 0.72);
  font-size: 0.95rem;
  font-weight: 850;
  text-decoration: none;
  backdrop-filter: blur(12px);

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #fde68a;
  }
`;

const EmailSignInForm = styled.form`
  display: grid;
  gap: 0.65rem;
`;

const EmailInput = styled.input`
  width: 100%;
  min-height: 48px;
  box-sizing: border-box;
  padding: 0.75rem 1rem;
  border: 1px solid rgba(226, 232, 240, 0.16);
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.68);
  color: #f8fafc;
  font: inherit;
  font-size: 1rem;
  font-weight: 800;

  &::placeholder {
    color: rgba(226, 232, 240, 0.54);
  }

  &:focus {
    border-color: #fbbf24;
    box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.16);
    outline: none;
  }
`;

const EmailButton = styled(DemoButton)`
  margin-top: 0.75rem;
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 20;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(2, 6, 23, 0.72);
  backdrop-filter: blur(12px);
`;

const EmailModal = styled.div`
  width: min(360px, 100%);
  padding: 1rem;
  border: 1px solid rgba(226, 232, 240, 0.18);
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(17, 24, 39, 0.98));
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.42);
`;

const ModalTitle = styled.h2`
  margin: 0 0 0.85rem;
  color: #f8fafc;
  font-size: 1.35rem;
  font-weight: 950;
  line-height: 1.15;
  text-align: center;
`;

const ModalActions = styled.div`
  display: grid;
  gap: 0.65rem;
  margin-top: 0.75rem;
`;

const ModalSubmitButton = styled(GoogleButton)`
  width: 100%;
  margin-top: 0;
`;

const ModalCancelButton = styled(DemoButton)`
  width: 100%;
  margin-top: 0;
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid ${({ $light }) => ($light ? 'rgba(248, 250, 252, 0.22)' : 'rgba(17, 24, 39, 0.18)')};
  border-top: 2px solid ${({ $light }) => ($light ? '#f8fafc' : '#111827')};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const GhostTypingSlot = styled.div`
  display: grid;
  align-content: center;
  gap: 0.45rem;
  min-height: 9.5rem;
  margin-top: 1.25rem;
`;

const GhostPledgeLine = styled.div`
  color: rgba(226, 232, 240, 0.64);
  font-size: clamp(0.95rem, 2.5vw, 1.12rem);
  font-weight: 900;
  line-height: 1.25;
`;

const GhostToken = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 1.8rem;
  padding: 0.15rem 0.5rem;
  border: 1px solid rgba(251, 191, 36, 0.26);
  border-radius: 999px;
  background: rgba(251, 191, 36, 0.12);
  color: #fbbf24;
  white-space: nowrap;
`;

const GhostTypingLine = styled.div`
  display: block;
  width: min(460px, 100%);
  margin: 0 auto;
  color: rgba(203, 213, 225, 0.5);
  font-size: clamp(0.95rem, 2.5vw, 1.15rem);
  font-weight: 800;
  text-align: center;
  line-height: 1.35;
  white-space: normal;
  overflow-wrap: anywhere;
`;

const TypingCursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 1.15em;
  margin-left: 2px;
  background: rgba(147, 197, 253, 0.62);
  vertical-align: -0.12em;
  animation: blink 0.85s step-end infinite;

  @keyframes blink {
    50% {
      opacity: 0;
    }
  }
`;

const sampleAppointments = [
  {
    name: 'Boss',
    location: 'Work',
    time: '8:30 AM',
    message: "i just woke up and i'm putting on my pants now lol, i missed the alarm. please don't fire me"
  },
  {
    name: 'Angela',
    location: 'the Gym',
    time: '7:15 AM',
    message: "i'm standing in my room wearing one sock and holding the other. the gym is not happening by 7:15"
  },
  {
    name: 'Cynthia',
    location: 'Home',
    time: '7:00 PM',
    message: "honey I'm having an affair with someone at the office. jk, but for reals i'm running late"
  },
  {
    name: 'Mrs. Gibson',
    location: 'school',
    time: '8:30 AM',
    message: "My dog ate my homework, and took a poop in my room, I'm stuck cleaning it up. "
  },
  {
    name: 'David',
    location: 'the office',
    time: '9:00 AM',
    message: "running behind. make up a lie to the bossman instead of telling him how lazy I am"
  },
  {
    name: 'Jessica',
    location: 'the library',
    time: '10:00 AM',
    message: "i'm still at home with my laptop open and zero shoes on. tell the girls what a lazy bones i am"
  },
  {
    name: 'Chris',
    location: 'therapy',
    time: '9:30 AM',
    message: "i'm running late...  just spent 10 min looking for the notebook where i wrote down how to not be late"
  },
  {
    name: 'Emily',
    location: 'the airport',
    time: '7:15 AM',
    message: "i'm in the bathroom with wet hair, brushing my teeth, and farting in the toilet. I don't think I'll be there on time. I owe you $25"
  },
  {
    name: 'Daniel',
    location: 'the train station',
    time: '8:30 AM',
    message: "Dude! I'm sooooo sorry but if you see this message, I'm not gonna make it. i owe you $10"
  },
  {
    name: 'Ashley',
    location: 'yoga class',
    time: '7:15 AM',
    message: 'i can see my yoga mat from the foot of my bed with bleary eyes. tell the girls im a lazy pos'
  },
  {
    name: 'Matthew',
    location: 'the dentist',
    time: '10:00 AM',
    message: "call my dentist and tell him that I think i'm an anti-dentite and the worst. and also i'm late"
  },
  {
    name: 'Amanda',
    location: 'the doctor',
    time: '9:30 AM',
    message: "i was literally leaving and then had to do the pocket-pat dance because my wallet vanished. found it in yesterday's jeans. i owe you $10"
  },
  {
    name: 'James',
    location: 'church',
    time: '10:00 AM',
    message: "James ur not gonna believe this but im cheating on the pastor's wife"
  },
  {
    name: 'Jennifer',
    location: 'the trailhead',
    time: '7:15 AM',
    message: "i'm still at home putting trail mix into a ziploc that counts as on time rite lulz. luv u"
  },
  {
    name: 'Robert',
    location: 'date night',
    time: '6:00 PM',
    message: "i know i promised you i'd be on time, but i've failed. will dessert cheer you up?"
  },
  {
    name: 'Nicole',
    location: 'daycare pickup',
    time: '5:45 PM',
    message: "i think i'm no longer a responsible parent lol, im late for the kids"
  },
  {
    name: 'Brian',
    location: 'the coffee shop',
    time: '8:30 AM',
    message: "i just want you to know i'm a fat ugly pig and don't deserve drink a cup in your holy presence"
  },
  {
    name: 'Lauren',
    location: 'the coworking space',
    time: '9:30 AM',
    message: "did i tell you how much i hate your bf? now that that's off my chest... i'm late"
  },
  {
    name: 'Kevin',
    location: 'the climbing gym',
    time: '6:00 PM',
    message: "If you're reading this email, I owe you $20. Please harrass me until you get paid"
  },
  {
    name: 'Megan',
    location: 'the bus stop',
    time: '8:30 AM',
    message: 'i missed the bus while holding my latte and making eye contact with the driver. humbling start'
  }
];

const shuffleItems = (items) => {
  const shuffled = [...items];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }

  return shuffled;
};

const Login = () => {
  const {
    login,
    guestLogin,
    sendEmailSignInLink,
    hasEmailSignInLink,
    completeEmailSignInLink,
    error: authError,
    user
  } = useAuth();
  const navigate = useNavigate();
  const [authLoading, setAuthLoading] = useState(null);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [emailLinkSent, setEmailLinkSent] = useState(false);
  const [emailFormOpen, setEmailFormOpen] = useState(false);
  const [needsEmailForLink, setNeedsEmailForLink] = useState(false);
  const [samplePledge, setSamplePledge] = useState(sampleAppointments[0]);
  const [typedShameMessage, setTypedShameMessage] = useState('');
  const isGoogleLoading = authLoading === 'google';
  const isGuestLoading = authLoading === 'guest';
  const isEmailLoading = authLoading === 'email';
  const isAuthLoading = authLoading !== null;
  const isSavingGuestAccount = user?.isAnonymous === true;

  useEffect(() => {
    if (!hasEmailSignInLink()) {
      return;
    }

    const completeLink = async () => {
      setAuthLoading('email');
      setError('');

      try {
        const result = await completeEmailSignInLink();
        navigate(result.redirectPath || '/dashboard');
      } catch (err) {
        const message = err?.message || 'Failed to complete email sign-in';

        if (message === 'Enter the email address you used for the sign-in link') {
          setNeedsEmailForLink(true);
          setEmailFormOpen(true);
        } else {
          setError(message);
        }
      } finally {
        setAuthLoading(null);
      }
    };

    completeLink();
  }, [completeEmailSignInLink, hasEmailSignInLink, navigate]);

  useEffect(() => {
    if (user) {
      return undefined;
    }

    let timeoutId;
    let cancelled = false;
    let currentIndex = 0;
    let isDeleting = false;
    let sampleQueue = shuffleItems(sampleAppointments);
    let currentSample = sampleQueue.pop();
    let fullText = currentSample.message;
    setSamplePledge(currentSample);

    const tick = () => {
      if (cancelled) {
        return;
      }

      currentIndex += isDeleting ? -1 : 1;
      setTypedShameMessage(fullText.slice(0, currentIndex));

      if (!isDeleting && currentIndex === fullText.length) {
        isDeleting = true;
        timeoutId = window.setTimeout(tick, 1600);
        return;
      }

      if (isDeleting && currentIndex === 0) {
        isDeleting = false;

        if (sampleQueue.length === 0) {
          sampleQueue = shuffleItems(sampleAppointments);
        }

        currentSample = sampleQueue.pop();
        fullText = currentSample.message;
        setSamplePledge(currentSample);
        timeoutId = window.setTimeout(tick, 450);
        return;
      }

      timeoutId = window.setTimeout(tick, isDeleting ? 28 : 58);
    };

    timeoutId = window.setTimeout(tick, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [user]);

  const handleLogin = async () => {
    setAuthLoading('google');
    setError('');

    try {
      await login();
      navigate('/dashboard');
    } catch (err) {
      console.error('Login failed:', err);
      setError(err.message);
    } finally {
      setAuthLoading(null);
    }
  };

  const handleGuest = async () => {
    setAuthLoading('guest');
    setError('');

    try {
      await guestLogin();
      navigate('/demo');
    } catch (err) {
      console.error('Guest login failed:', err);
      if (err.code === 'auth/admin-restricted-operation' || err.code === 'auth/operation-not-allowed') {
        setError('Guest mode is not enabled yet. Enable Anonymous sign-in in Firebase Authentication.');
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(err.message || 'Failed to continue as guest');
      }
    } finally {
      setAuthLoading(null);
    }
  };

  const handleOpenEmailModal = () => {
    setEmailFormOpen(true);
    setError('');
  };

  const handleCloseEmailModal = () => {
    if (isEmailLoading || needsEmailForLink) {
      return;
    }

    setEmailFormOpen(false);
  };

  const handleEmailSignIn = async (event) => {
    event.preventDefault();

    setAuthLoading('email');
    setError('');
    setEmailLinkSent(false);

    try {
      if (needsEmailForLink) {
        const result = await completeEmailSignInLink(email);
        navigate(result.redirectPath || '/dashboard');
        return;
      }

      await sendEmailSignInLink(email, '/dashboard');
      setEmailLinkSent(true);
    } catch (err) {
      console.error('Email sign in failed:', err);
      setError(err?.message || 'Failed to send sign-in link');
    } finally {
      setAuthLoading(null);
    }
  };

  return (
    <LoginPage>
      <FaqLink to="/faq">FAQ</FaqLink>
      <LoginShell>
        <BrandLogo src={placePledgeLogoBold} alt="PlacePledge" />
        <BrandSubheading>
          Location-based accountability for better habits.
        </BrandSubheading>
        <LoginCard>
          {!user && (
            <GhostTypingSlot>
              <GhostPledgeLine>
                I pledge to be at <GhostToken>{samplePledge.location}</GhostToken> by <GhostToken>{samplePledge.time}</GhostToken> or else send <GhostToken>{samplePledge.name}</GhostToken>:
              </GhostPledgeLine>
              <GhostTypingLine>
                “{typedShameMessage}
                <TypingCursor />
                ”
              </GhostTypingLine>
            </GhostTypingSlot>
          )}

          <GoogleButton onClick={handleLogin} disabled={isAuthLoading}>
            {isGoogleLoading ? (
              <>
                <LoadingSpinner />
                Signing in...
              </>
            ) : (
              <>
                <img
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  alt="Google logo"
                />
                {isSavingGuestAccount ? 'Sign up with Google' : 'Sign in with Google'}
              </>
            )}
          </GoogleButton>

          <EmailButton type="button" onClick={handleOpenEmailModal} disabled={isAuthLoading}>
            {needsEmailForLink
              ? 'Finish email sign in'
              : isSavingGuestAccount
                ? 'Sign up with email'
                : 'Sign in with email'}
          </EmailButton>

          {isSavingGuestAccount ? (
            <DemoButton type="button" onClick={() => navigate('/dashboard')} disabled={isAuthLoading}>
              Back to demo
            </DemoButton>
          ) : (
            <DemoButton type="button" onClick={handleGuest} disabled={isAuthLoading}>
              {isGuestLoading ? (
                <>
                  <LoadingSpinner $light />
                  Continuing...
                </>
              ) : (
                'Preview as guest'
              )}
            </DemoButton>
          )}

          {(error || authError) && (
            <Message $error>
              {error || authError}
            </Message>
          )}
        </LoginCard>
      </LoginShell>
      {(emailFormOpen || needsEmailForLink) && (
        <ModalBackdrop onMouseDown={handleCloseEmailModal}>
          <EmailModal
            role="dialog"
            aria-modal="true"
            aria-labelledby="email-sign-in-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <ModalTitle id="email-sign-in-title">
              {needsEmailForLink
                ? 'Finish email sign in'
                : isSavingGuestAccount
                  ? 'Sign up with email'
                  : 'Sign in with email'}
            </ModalTitle>
            <EmailSignInForm onSubmit={handleEmailSignIn}>
              <EmailInput
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={email}
                placeholder="you@example.com"
                aria-label="Email address"
                onChange={(event) => {
                  setEmail(event.target.value);
                  setEmailLinkSent(false);
                }}
                disabled={isAuthLoading && !isEmailLoading}
                autoFocus
                required
              />
              <ModalActions>
                <ModalSubmitButton type="submit" disabled={isAuthLoading && !isEmailLoading}>
                  {isEmailLoading ? (
                    <>
                      <LoadingSpinner />
                      {needsEmailForLink ? 'Signing in...' : 'Sending...'}
                    </>
                  ) : needsEmailForLink ? (
                    'Finish sign in'
                  ) : (
                    'Send sign-in link'
                  )}
                </ModalSubmitButton>
                {!needsEmailForLink && (
                  <ModalCancelButton type="button" onClick={handleCloseEmailModal} disabled={isEmailLoading}>
                    Cancel
                  </ModalCancelButton>
                )}
              </ModalActions>
            </EmailSignInForm>
            {emailLinkSent && (
              <Message>
                Check your email for a sign-in link.
              </Message>
            )}
            {(error || authError) && (
              <Message $error>
                {error || authError}
              </Message>
            )}
          </EmailModal>
        </ModalBackdrop>
      )}
    </LoginPage>
  );
};

export default Login;
